import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { RichTextEditor } from "../common/RichTextEditor";
import type { Trip, TravelPartyMember, Connection } from "../../types";
import { Timestamp } from "firebase/firestore";
import { dateFromDayKey, dayKeyOf } from "../../utils/calendarDay";

interface TripFormProps {
  initial?: Partial<Trip>;
  onSubmit: (data: {
    name: string;
    startDate: Date;
    endDate: Date;
    notes: string;
    travelParty?: Record<string, TravelPartyMember>;
    /** The party as the form opened with it, so an edit can write only its difference. */
    openedTravelParty?: Record<string, TravelPartyMember>;
  }) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

function toDateInputValue(ts: Timestamp | undefined): string {
  return dayKeyOf(ts) ?? "";
}

export function TripForm({ initial, onSubmit, onCancel, submitLabel = "Create Trip" }: TripFormProps) {
  const { userProfile } = useAuth();
  const [name, setName] = useState(initial?.name || "");
  const [startDate, setStartDate] = useState(toDateInputValue(initial?.startDate));
  const [endDate, setEndDate] = useState(toDateInputValue(initial?.endDate));
  const [notes, setNotes] = useState(initial?.notes || "");
  const [travelParty, setTravelParty] = useState<Record<string, TravelPartyMember>>(
    initial?.travelParty ? { ...initial.travelParty } : {}
  );
  // What the party looked like when the form opened. Submitted alongside the
  // edited copy so the trip page can write just the members added or removed —
  // writing the whole copy back would undo anything that changed meanwhile.
  const [openedTravelParty] = useState<Record<string, TravelPartyMember>>(
    () => initial?.travelParty ?? {},
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connections = Object.entries(userProfile?.connections || {}) as [string, Connection][];
  const existingConnectionIds = new Set(
    Object.values(travelParty).map((m) => m.connectionId).filter((id): id is string => id != null)
  );
  const availableConnections = connections.filter(([id]) => !existingConnectionIds.has(id));
  const partyEntries = Object.entries(travelParty);

  const handleAddMember = (connectionId: string, conn: Connection) => {
    setTravelParty((prev) => ({
      ...prev,
      [connectionId]: {
        displayName: conn.displayName,
        avatarUrl: conn.avatarUrl,
        userId: conn.linkedUserId,
        connectionId,
        role: "member",
        locationSharingEnabled: null,
        status: conn.linkedUserId ? "confirmed" : "pending",
      },
    }));
  };

  const handleRemoveMember = (key: string) => {
    setTravelParty((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Trip name is required.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Start and end dates are required.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError("End date must be on or after start date.");
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        // Calendar days, stored so no phone's zone can move them.
        startDate: dateFromDayKey(startDate),
        endDate: dateFromDayKey(endDate),
        notes: notes.trim(),
        travelParty,
        openedTravelParty,
      });
    } catch {
      setError("Failed to save trip. Please try again.");
      setSaving(false);
    }
  };

  return (
    <form className="form" onSubmit={handleSubmit}>
      {error && <div className="form-error">{error}</div>}

      <label className="form-field">
        <span className="form-label">Trip Name</span>
        <input
          type="text"
          className="form-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Walt Disney World 2026"
          autoFocus
        />
      </label>

      <div className="form-row">
        <label className="form-field">
          <span className="form-label">Start Date</span>
          <input
            type="date"
            className="form-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label className="form-field">
          <span className="form-label">End Date</span>
          <input
            type="date"
            className="form-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
      </div>

      <div className="form-field">
        <span className="form-label">Notes</span>
        <RichTextEditor
          value={notes}
          onChange={setNotes}
          placeholder="Any notes about this trip..."
        />
      </div>

      {/* Travel Party */}
      <div className="form-field">
        <span className="form-label">Travel Party ({partyEntries.length})</span>

        {partyEntries.length > 0 && (
          <div className="travel-party-edit-list">
            {partyEntries.map(([key, member]) => (
              <div key={key} className="travel-party-edit-item">
                <div className="party-avatar">
                  {member.avatarUrl ? (
                    <img src={member.avatarUrl} alt={member.displayName} />
                  ) : (
                    <span>{member.displayName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <span className="party-name">{member.displayName}</span>
                <span className="party-role">{member.role}</span>
                {member.role !== "organizer" && (
                  <button
                    type="button"
                    className="btn-icon"
                    title="Remove member"
                    onClick={() => handleRemoveMember(key)}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {availableConnections.length > 0 && (
          <div className="travel-party-add">
            {availableConnections.map(([id, conn]) => (
              <button
                key={id}
                type="button"
                className="travel-party-add-btn"
                onClick={() => handleAddMember(id, conn)}
              >
                <div className="party-avatar">
                  {conn.avatarUrl ? (
                    <img src={conn.avatarUrl} alt={conn.displayName} />
                  ) : (
                    <span>{conn.displayName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <span>{conn.displayName}</span>
                <span className="travel-party-add-plus">+</span>
              </button>
            ))}
          </div>
        )}

        {partyEntries.length === 0 && availableConnections.length === 0 && (
          <p className="text-muted">No connections yet. Add connections from the Connections page.</p>
        )}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn" disabled={saving}>
          {saving ? "Saving..." : submitLabel}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
